import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
} from '@material-ui/core';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import { usePOS } from '../../../contexts/POSContext';
import { formattedCurrency } from '../../../utils/utils';
import UIDialogTitle from '../../UI/Dialog/Title/UIDialogTitle';

const POSCouponModal = ({ open, onClose }) => {
  const {
    promotions,
    selectedPromotion,
    setSelectedPromotion,
    appliedCoupon,
    validateCoupon,
    isDarkMode,
  } = usePOS();

  const [couponCode, setCouponCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setCouponCode('');
      setFeedback(null);
      setTimeout(() => {
        if (inputRef.current) inputRef.current.focus();
      }, 100);
    }
  }, [open]);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setLoading(true);
    setFeedback(null);

    const result = await validateCoupon(couponCode.trim());
    setLoading(false);

    if (result.success) {
      setFeedback({ type: 'success', text: result.message });
      setTimeout(() => {
        onClose();
      }, 1000);
    } else {
      setFeedback({ type: 'error', text: result.message });
    }
  };

  const handleSelectPromotion = (promo) => {
    setSelectedPromotion(promo);
    onClose();
  };

  const handleRemovePromotion = () => {
    setSelectedPromotion(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: isDarkMode ? 'pos-dark-paper' : '' }}
    >
      <UIDialogTitle onClose={onClose}>
        Promoções e Cupons de Desconto [F8]
      </UIDialogTitle>

      <DialogContent dividers style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label style={{ fontSize: '0.85rem', color: '#616161', display: 'block', marginBottom: 6 }}>
            Digitar Código de Cupom:
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <TextField
              inputRef={inputRef}
              placeholder="Ex: PROMO10"
              variant="outlined"
              fullWidth
              size="small"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleApplyCoupon();
                }
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleApplyCoupon}
              disabled={loading || !couponCode.trim()}
              style={{ minWidth: 100, backgroundColor: '#17497F', textTransform: 'none' }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Aplicar'}
            </Button>
          </div>

          {feedback && (
            <div
              style={{
                marginTop: 8,
                fontSize: '0.85rem',
                fontWeight: 600,
                color: feedback.type === 'success' ? '#2e7d32' : '#c62828',
              }}
            >
              {feedback.text}
            </div>
          )}
        </div>

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: '0.85rem', color: isDarkMode ? '#cbd5e1' : '#616161', fontWeight: 600 }}>Promoções Ativas:</span>
            {selectedPromotion && (
              <Button size="small" onClick={handleRemovePromotion} style={{ color: '#ef4444', textTransform: 'none', fontWeight: 600 }}>
                Remover Promoção
              </Button>
            )}
          </div>

          <div style={{ maxHeight: 200, overflowY: 'auto', border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0', borderRadius: 4 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {promotions.filter((p) => !p.couponCode).map((promo) => {
                  const isSelected = selectedPromotion?.id === promo.id;
                  const discountLabel = promo.percentage > 0
                    ? `${promo.percentage}% OFF`
                    : (promo.discount > 0 ? `${formattedCurrency(promo.discount)} OFF` : 'Especial');

                  return (
                    <tr
                      key={promo.id}
                      style={{
                        cursor: 'pointer',
                        backgroundColor: isSelected
                          ? (isDarkMode ? 'rgba(34, 197, 94, 0.15)' : '#e8f5e9')
                          : 'transparent',
                        borderBottom: isDarkMode ? '1px solid #1e293b' : '1px solid #f0f0f0',
                      }}
                      onClick={() => handleSelectPromotion(promo)}
                    >
                      <td style={{ width: 36, padding: '8px 10px', textAlign: 'center' }}>
                        <LocalOfferIcon style={{ color: isSelected ? (isDarkMode ? '#4ade80' : '#2e7d32') : (isDarkMode ? '#64748b' : '#9e9e9e') }} />
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        <div style={{ fontWeight: 600, color: isSelected ? (isDarkMode ? '#4ade80' : '#2e7d32') : (isDarkMode ? '#f8fafc' : '#212121') }}>
                          {promo.name}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: isDarkMode ? '#94a3b8' : '#757575', marginTop: 2 }}>
                          {promo.description || 'Regras aplicáveis'}
                        </div>
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: isDarkMode ? '#4ade80' : '#2e7d32', padding: '8px 12px' }}>
                        {discountLabel}
                      </td>
                    </tr>
                  );
                })}
                {promotions.length === 0 && (
                  <tr>
                    <td colSpan={3} style={{ textAlign: 'center', padding: 16, color: isDarkMode ? '#94a3b8' : '#9e9e9e' }}>
                      Nenhuma promoção ativa cadastrada.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </DialogContent>

      <DialogActions style={{ padding: '8px 16px' }}>
        <Button onClick={onClose} color="default">
          Fechar [Esc]
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default POSCouponModal;
